import {
  UserSearchIcon,
  LandmarkIcon,
  NetworkIcon,
  BriefcaseIcon,
  MessagesSquareIcon,
  CompassIcon,
} from 'lucide-react';
import type { Service } from '../types/content';

export const services: Service[] = [
  {
    id: 'executive-search',
    title: 'Executive Search',
    summary: 'Identifying experienced leaders for senior executive positions.',
    icon: UserSearchIcon,
    overview:
      'A structured, research-led search for senior executive appointments. We work closely with leadership teams and boards to understand the role, the organization and the context in which a new leader will operate — then identify, approach and assess relevant candidates with discretion.',
    involves: [
      'Role and context briefing with key stakeholders',
      'Defining the candidate profile and assessment criteria',
      'Market research and identification of potential candidates',
      'Confidential approach and initial conversations',
      'Structured assessment against agreed criteria',
      'Shortlist presentation and support through interviews',
    ],
    suitedTo: 'C-suite, divisional leadership and other senior executive appointments.',
  },
  {
    id: 'board-search',
    title: 'Board & Non-Executive Director Search',
    summary:
      'Supporting organizations seeking board-level experience, governance knowledge, and strategic oversight.',
    icon: LandmarkIcon,
    overview:
      'Board appointments call for a different lens: governance experience, independence, committee capability and the ability to contribute to strategic oversight. We help organizations define what the board needs and identify individuals whose experience may complement it.',
    involves: [
      'Review of board composition and capability needs',
      'Definition of the role, time commitment and committee requirements',
      'Research across executive and non-executive talent pools',
      'Discreet approach to potential candidates',
      'Assessment of governance experience and independence considerations',
      'Support for the chair and nominations process',
    ],
    suitedTo: 'Chairs, non-executive directors, independent directors and committee chairs.',
  },
  {
    id: 'talent-mapping',
    title: 'Leadership Talent Mapping',
    summary: 'Researching relevant talent markets and identifying potential leadership candidates.',
    icon: NetworkIcon,
    overview:
      'Talent mapping provides a structured view of a defined leadership market — who holds relevant roles, where expertise is concentrated and how the market is organized. It can inform a specific search or longer-term succession and workforce planning.',
    involves: [
      'Agreeing the scope: functions, sectors and geographies',
      'Identifying organizations and roles of relevance',
      'Building a structured map of potential leadership talent',
      'Observations on market composition and availability',
      'A confidential report to support planning decisions',
    ],
    suitedTo: 'Succession planning, market entry, organizational design and pre-search research.',
  },
  {
    id: 'specialized-recruitment',
    title: 'Specialized Professional Recruitment',
    summary:
      'Helping employers identify professionals with relevant technical and functional expertise.',
    icon: BriefcaseIcon,
    overview:
      'Some roles depend on deep technical or functional expertise that is difficult to find through general channels. We take time to understand the specialist requirement in detail, so that outreach and assessment are relevant to the work itself.',
    involves: [
      'Detailed briefing on technical and functional requirements',
      'Targeted research within specialist communities',
      'Professional outreach to relevant individuals',
      'Initial assessment of expertise and motivation',
      'Coordination of the interview process',
    ],
    suitedTo: 'Senior specialist, technical and functional roles.',
  },
  {
    id: 'candidate-sourcing',
    title: 'Candidate Sourcing & Engagement',
    summary:
      'Identifying and engaging potential candidates through professional research and outreach.',
    icon: MessagesSquareIcon,
    overview:
      'For organizations running their own hiring process, we can support the identification and engagement stage — researching potential candidates and conducting professional, confidential outreach on your behalf.',
    involves: [
      'Agreeing target profiles and outreach messaging',
      'Research to identify potential candidates',
      'Professional, confidential outreach',
      'Initial conversations to confirm interest and relevance',
      'Handover of engaged candidates to your process',
    ],
    suitedTo: 'In-house talent teams seeking additional research and engagement capacity.',
  },
  {
    id: 'talent-advisory',
    title: 'Talent Advisory',
    summary:
      'Supporting organizations with search strategy, role definition, and talent market insights.',
    icon: CompassIcon,
    overview:
      'Before a search begins — or alongside one — organizations often need clarity on what the role should be and what the market can realistically offer. Our advisory support helps shape those decisions before they become commitments.',
    involves: [
      'Search strategy and approach design',
      'Role definition and candidate specification',
      'Talent market insight for specific roles',
      'Input on assessment criteria and interview design',
      'Guidance on candidate experience and communication',
    ],
    suitedTo: 'Leadership teams and HR functions planning significant hires.',
  },
];
